import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { CommunicationTemplateChannel } from '@client/components/communication-templates/CommunicationTemplateChannel';
import { CommunicationTemplateFilters } from '@client/components/communication-templates/CommunicationTemplateFilters';
import { type Filter, FilterSchema, isFilterValueEmpty, isWhereClauseEmpty } from '@client/components/filters';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { formatCommTemplateRelatedTo } from '@client/utils/formatters/comm-template';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { Tag } from 'antd';
import { useState } from 'react';
import { z } from 'zod';

export const Route = createFileRoute('/console/$orgId/configuration/org-settings/communication/comm-templates')({
    staticData: {
        metadata: {
            title: 'Communication templates',
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <CommunicationTemplateSearch />
        </PageController>
    );
}

type QueryParams = RouterInputs['commTemplate']['listCommTemplates'];
/** The table's URL state: the query, plus the filter bar's model its `where` was built from. */
type TemplatesView = QueryParams & { filters?: Filter[] };

const FiltersSchema = z.array(FilterSchema);

/** The filter model carried by parameters handed back by the table, or restored from the URL. */
function parseFilters(value: unknown): Filter[] | undefined {
    const parsed = FiltersSchema.safeParse(value);
    return parsed.success ? parsed.data : undefined;
}

/** Leaves out an empty filter model and `where`, so clearing the filters returns the URL to the defaults. */
function withFilters(view: TemplatesView, where: QueryParams['where'], filters: Filter[]): TemplatesView {
    const { where: _where, filters: _filters, ...rest } = view;
    const meaningful = filters.filter((filter) => !isFilterValueEmpty(filter.operator, filter.value));
    return {
        ...rest,
        ...(where && !isWhereClauseEmpty(where) ? { where } : {}),
        ...(meaningful.length ? { filters: meaningful } : {}),
    };
}

const columnHelper = createColumnHelper<
    RouterOutputs['commTemplate']['listCommTemplates']['rows'][number],
    QueryParams['orderBy']
>();

function CommunicationTemplateSearch() {
    const orgId = useOrgId();
    const { trpcUtils } = useTrpc();
    const { mutate } = trpc.commTemplate.deleteCommTemplate.useMutation();
    const navigate = useNavigate();

    const [view, setView] = useState<TemplatesView>({
        orderBy: { id: 'desc' },
        limit: DEFAULT_PAGE_SIZE,
        offset: 0,
    });
    // Bumped when the filter model changes from outside the filter bar (restored from the URL, or reset
    // to the defaults), so the bar mounts again showing it.
    const [filterBarKey, setFilterBarKey] = useState(0);
    const { filters, ...input } = view;

    const { data, isPending, error, refetch, isFetching } = trpc.commTemplate.listCommTemplates.useQuery(input);

    const tableProps = usePrismaTable({
        data: data?.rows,
        total: data?.total,
        loading: isPending,
        rowKey: 'id',
        queryParams: view,
        onParamsChange: (params) => {
            // Sort and page changes carry the current model along unchanged.
            const carried = 'filters' in params ? params.filters : undefined;
            if (carried === filters) {
                setView(params);
                return;
            }
            const { filters: _filters, ...rest } = { ...params, filters: undefined };
            const next = parseFilters(carried);
            setView(next ? { ...rest, filters: next } : rest);
            setFilterBarKey((key) => key + 1);
        },
        // Filters (with the filter bar's model), sort and page survive a refresh and a shared link (CUR-1341).
        urlKey: 'templates',
        queryError: error,
        emptyState: { noRecordsTitle: 'No communication templates yet' },
        columns: [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'ID',
                sortable: 'id',
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
                sortable: 'name',
            }),
            columnHelper.accessor('channel', {
                id: 'channel',
                header: 'Channel',
                sortable: 'channel',
                cell: ({ row }) => <CommunicationTemplateChannel channel={row.channel} />,
            }),
            columnHelper.accessor('relatedTo', {
                id: 'relatedTo',
                header: 'Related to',
                sortable: 'relatedTo',
                cell: ({ row }) => formatCommTemplateRelatedTo(row.relatedTo),
            }),
            columnHelper.accessor('status', {
                id: 'status',
                header: 'Status',
                sortable: 'status',
                cell: ({ row }) => (row.status ? <StatusBadge status={row.status} module="commTemplate" /> : null),
            }),
            columnHelper.accessor('version', {
                id: 'version',
                header: 'Version',
                sortable: 'version',
                cell: ({ row }) => (row.version ? <Tag>v{row.version}</Tag> : null),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Button.Delete
                        onConfirm={(callback) => {
                            mutate(
                                { id: row.id },
                                {
                                    onSuccess() {
                                        callback();
                                        void trpcUtils.commTemplate.invalidate();
                                    },
                                    onError(error) {
                                        errorMessage.show(error);
                                    },
                                },
                            );
                        }}
                    />
                ),
            }),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} retrying={isFetching} />;
    }

    return (
        <>
            <Outlet />

            <TableToolbar
                filters={
                    <CommunicationTemplateFilters
                        key={filterBarKey}
                        initialFilters={filters ?? []}
                        onChange={(where, next) =>
                            setView((prev) => ({ ...withFilters(prev, where, next), offset: 0 }))
                        }
                    />
                }
                actions={
                    <Button.Add.Link
                        to="/console/$orgId/configuration/org-settings/communication/comm-templates/add"
                        params={{ orgId }}
                        variant="primary"
                        label="New communication template"
                    />
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id }) => ({
                    className: 'cursor-pointer',
                    onClick(e) {
                        navigate(
                            {
                                to: '/console/$orgId/configuration/org-settings/communication/comm-templates/edit/$communicationTemplateId',
                                params: { orgId, communicationTemplateId: id },
                            },
                            e,
                        );
                    },
                })}
            />
        </>
    );
}
